import { useEffect, useRef, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import * as Sentry from "@sentry/react";
import Button from "@/components/ui/Button";
import ScriptText from "@/components/ui/ScriptText";

interface WaitingListFormData {
  email: string;
  note: string;
  marketingOptIn: boolean;
  website: string; // honeypot
}

const WL_LIMITS = { email: 254, note: 500 } as const;

function CharCount({ value, max }: { value: string | undefined; max: number }) {
  const len = value?.length ?? 0;
  const ratio = len / max;
  const color =
    ratio >= 1
      ? "text-faded-terracotta"
      : ratio >= 0.85
        ? "text-faded-terracotta/70"
        : "text-warm-stone/40";
  return (
    <p className={`mt-2 text-right font-body text-xs tabular-nums transition-colors duration-300 ${color}`}>
      {len} / {max}
    </p>
  );
}

interface WaitingListModalProps {
  open: boolean;
  onClose: () => void;
  /** Retreat title shown in the modal heading, e.g. "The Art of Presence" */
  retreatTitle?: string;
  /** Region used in the placeholder/body copy, e.g. "Provence" */
  region?: string;
}

export default function WaitingListModal({
  open,
  onClose,
  retreatTitle,
  region,
}: WaitingListModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Controls whether the DOM element is mounted
  const [mounted, setMounted] = useState(false);
  // Controls the visible (opacity 1) state — delayed slightly after mount
  const [visible, setVisible] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm<WaitingListFormData>();

  const onSubmit = async (data: WaitingListFormData) => {
    setSubmitError(false);
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: data.email,
          note: data.note,
          website: data.website,
          marketingOptIn: data.marketingOptIn,
          retreatTitle,
          region: region || "Provence",
        }),
      });
      const result = await res.json();
      if (!res.ok || !result.success) {
        setSubmitError(true);
        const error = new Error(result.error || "Submission failed");
        Sentry.captureException(error, {
          tags: { form: "waitlist" },
          extra: { retreatTitle },
        });
        throw error;
      }
    } catch (error) {
      setSubmitError(true);
      if (!(error instanceof Error && error.message.includes("Submission failed"))) {
        Sentry.captureException(error, {
          tags: { form: "waitlist" },
          extra: { retreatTitle },
        });
      }
      throw error;
    }
  };

  const handleClose = useCallback(() => {
    // Start fade-out
    setVisible(false);
    // Unmount after transition completes, then reset form
    setTimeout(() => {
      onClose();
      reset();
      setSubmitError(false);
    }, 400);
  }, [onClose, reset]);

  // Mount/unmount + fade lifecycle
  useEffect(() => {
    if (open) {
      setMounted(true);
      // Trigger fade-in on next frame so the transition plays
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setVisible(true);
          // Scroll the panel to top so mobile always starts at the beginning
          panelRef.current?.scrollTo(0, 0);
        });
      });
    } else {
      setVisible(false);
      setMounted(false);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!mounted) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mounted, handleClose]);

  // Lock body scroll when open, compensating for scrollbar width to prevent layout shift
  useEffect(() => {
    if (mounted) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
    };
  }, [mounted]);

  if (!mounted) return null;

  const regionName = region || "Provence";
  const placeholderText = `What draws you to ${regionName}?`;
  const bodyText = retreatTitle
    ? `Leave your email and we'll reach out when registration opens for ${retreatTitle}. If you'd like, share a little about yourself — we love hearing what draws you to ${regionName}.`
    : `Leave your email and we'll reach out when registration opens. If you'd like, share a little about yourself — we love hearing what draws you to ${regionName}.`;

  const labelClasses =
    "block mb-2 font-serif text-sm tracking-wider uppercase text-warm-stone";
  const inputClasses =
    "w-full border-0 border-b border-warm-stone/40 bg-transparent px-0 py-4 font-body text-lg text-soft-charcoal placeholder:text-warm-stone/60 focus:border-faded-terracotta focus:outline-none transition-colors duration-300";

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-start justify-center lg:items-center lg:px-4"
      onClick={(e) => {
        if (e.target === overlayRef.current) handleClose();
      }}
    >
      {/* Backdrop — hidden on mobile (panel is full-screen) */}
      <div
        className="absolute inset-0 hidden bg-soft-charcoal/40 backdrop-blur-sm transition-opacity duration-400 ease-out lg:block"
        style={{ opacity: visible ? 1 : 0 }}
      />

      {/* Panel — full-screen & scrollable on mobile, centered card on desktop */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Join the waiting list"
        className="relative flex w-full flex-col overflow-y-auto bg-parchment p-8 shadow-xl transition-all duration-400 ease-out max-h-full min-h-dvh lg:min-h-0 lg:max-w-lg lg:rounded-sm lg:p-12"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(12px)",
        }}
      >
        {/* Mobile: back button */}
        <button
          onClick={handleClose}
          className="mb-6 flex items-center gap-2 self-start font-body text-sm tracking-wider text-warm-stone transition-colors duration-300 hover:text-soft-charcoal cursor-pointer lg:hidden"
          aria-label="Go back"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M10 2L4 8l6 6" />
          </svg>
          Back
        </button>

        {/* Desktop: close X */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 hidden h-8 w-8 items-center justify-center text-warm-stone transition-colors duration-300 hover:text-soft-charcoal cursor-pointer lg:flex"
          aria-label="Close"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M1 1l16 16M17 1L1 17" />
          </svg>
        </button>

        {isSubmitSuccessful ? (
          <div className="py-8 text-center">
            <ScriptText className="mb-4 block text-3xl lg:text-4xl">
              Thank You
            </ScriptText>
            <p className="mx-auto max-w-sm font-body leading-relaxed text-soft-charcoal/80">
              We've added you to the waiting list. We'll be in touch as details
              come together — we can't wait to share this with you.
            </p>
            <button
              onClick={handleClose}
              className="mt-8 font-serif text-sm tracking-wider uppercase text-faded-terracotta transition-colors duration-300 hover:text-fig cursor-pointer"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <ScriptText className="mb-2 block text-2xl lg:text-3xl">
              Join the Waiting List
            </ScriptText>
            <p className="mb-10 font-body text-sm leading-relaxed text-soft-charcoal/60">
              {bodyText}
            </p>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-8"
              noValidate
            >
              {/* Honeypot — visually hidden, bots fill it */}
              <div className="absolute opacity-0 h-0 overflow-hidden" aria-hidden="true">
                <label htmlFor="wl-website">Website</label>
                <input
                  id="wl-website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  {...register("website")}
                />
              </div>

              <div>
                <label htmlFor="wl-email" className={labelClasses}>
                  Email
                </label>
                <input
                  id="wl-email"
                  type="email"
                  placeholder="your@email.com"
                  autoFocus
                  className={inputClasses}
                  maxLength={WL_LIMITS.email}
                  {...register("email", {
                    required: "Please enter your email",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Please enter a valid email address",
                    },
                  })}
                />
                {errors.email && (
                  <p className="mt-2 text-xs text-faded-terracotta">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="wl-note" className={labelClasses}>
                  Your Story{" "}
                  <span className="normal-case tracking-normal text-warm-stone/50">
                    (optional)
                  </span>
                </label>
                <textarea
                  id="wl-note"
                  rows={3}
                  placeholder={placeholderText}
                  maxLength={WL_LIMITS.note}
                  className={`${inputClasses} resize-none`}
                  {...register("note")}
                />
                <CharCount value={watch("note")} max={WL_LIMITS.note} />
              </div>

              {/* Marketing opt-in */}
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0 appearance-none rounded-sm border border-warm-stone/40 bg-transparent checked:border-faded-terracotta checked:bg-faded-terracotta transition-colors duration-300 cursor-pointer"
                  {...register("marketingOptIn")}
                />
                <span className="font-body text-sm leading-relaxed text-soft-charcoal/70">
                  Keep me in the loop — I'd love to hear about upcoming retreats
                  and new experiences as they're announced.
                </span>
              </label>

              {submitError && (
                <p className="text-sm text-faded-terracotta">
                  Something went wrong — please try again.
                </p>
              )}

              <Button type="submit" variant="filled" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Join the List"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
